/**
 * QUOTATION ITEM SLICE — le righe del preventivo aperto.
 *
 * Tiene le righe di **un** preventivo per volta, non una cache globale: le
 * righe si guardano solo dentro il loro preventivo, e non esiste una schermata
 * che ne mostri di preventivi diversi insieme. Da qui `quotationId` nello
 * stato, che dice di chi sono le righe che ci stanno adesso.
 *
 * Il campo serve anche a evitare il lampeggio: cambiando preventivo, le righe
 * del precedente sparirebbero solo all'arrivo della risposta, e per un istante
 * il nuovo preventivo mostrerebbe le righe del vecchio.
 */

import { createSlice } from '@reduxjs/toolkit';
import type { QuotationItem } from './quotationItemMappers';
import {
  createQuotationItem,
  deleteQuotationItem,
  fetchQuotationItems,
  reorderQuotationItems,
  updateQuotationItem,
} from './quotationItemThunks';

export interface QuotationItemState {
  /** Preventivo a cui appartengono le righe in `items`. Zero se nessuno. */
  quotationId: number;
  items: QuotationItem[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: QuotationItemState = {
  quotationId: 0,
  items: [],
  status: 'idle',
  error: null,
};

/** Le righe si mostrano nell'ordine dichiarato dal backend: per posizione. */
const byPosition = (a: QuotationItem, b: QuotationItem) => a.position - b.position;

export const quotationItemSlice = createSlice({
  name: 'quotationItems',
  initialState,
  reducers: {
    clearQuotationItems() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuotationItems.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        // Cambio di preventivo: si svuota subito invece di aspettare la
        // risposta, o per un istante si vedrebbero le righe di quello prima.
        if (state.quotationId !== action.meta.arg) {
          state.quotationId = action.meta.arg;
          state.items = [];
        }
      })
      .addCase(fetchQuotationItems.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.quotationId = action.payload.quotationId;
        state.items = [...action.payload.items].sort(byPosition);
      })
      .addCase(fetchQuotationItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(createQuotationItem.fulfilled, (state, action) => {
        if (state.quotationId !== action.payload.quotationId) return;
        state.items.push(action.payload);
        state.items.sort(byPosition);
      })
      .addCase(updateQuotationItem.fulfilled, (state, action) => {
        const idx = state.items.findIndex(
          (i) => i.quotationItemId === action.payload.quotationItemId,
        );
        if (idx !== -1) state.items[idx] = action.payload;
        state.items.sort(byPosition);
      })
      // Riordino OTTIMISTICO: le posizioni si riscrivono all'istante, prima che
      // il server risponda. Aspettare vorrebbe dire vedere la riga tornare al
      // suo posto per il tempo della chiamata, e il trascinamento sembrerebbe
      // non aver fatto presa.
      .addCase(reorderQuotationItems.pending, (state, action) => {
        const positions = new Map(
          action.meta.arg.quotationItemIds.map((id, index) => [id, index + 1]),
        );

        state.items = state.items
          .map((item) => {
            const position = positions.get(item.quotationItemId);
            return position === undefined ? item : { ...item, position };
          })
          .sort(byPosition);
      })
      .addCase(reorderQuotationItems.fulfilled, (state, action) => {
        if (state.quotationId !== action.payload.quotationId) return;
        // Le righe come sono finite davvero sul database, non come le avevamo
        // immaginate: la risposta le porta gia' tutte, e non serve rileggerle.
        state.items = [...action.payload.items].sort(byPosition);
      })
      .addCase(reorderQuotationItems.rejected, (state, action) => {
        // Lo stato resta quello ottimistico, ed e' sbagliato: a rimetterlo a
        // posto e' la rilettura che lancia chi ha chiamato il thunk. Qui si
        // registra solo il perche', che l'utente deve poter leggere.
        state.error = action.payload as string;
      })
      .addCase(deleteQuotationItem.fulfilled, (state, action) => {
        state.items = state.items.filter(
          (i) => i.quotationItemId !== action.payload.quotationItemId,
        );
      });
  },
});

export const { clearQuotationItems } = quotationItemSlice.actions;

export const quotationItemReducer = quotationItemSlice.reducer;
